import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { CurrencySwitcher } from '@/components/layout/CurrencySwitcher';
import { PricingCalculator } from '@/components/pricing/PricingCalculator';
import { billingApi } from '@/lib/billing';
import { useDocumentMeta } from '@/lib/useDocumentMeta';
import {
  currentDisplayCurrency,
  fromUsd,
  formatDisplayCurrency,
} from '@/lib/displayCurrency';

type PlanKey = 'free' | 'growth' | 'global' | 'enterprise';

interface Plan {
  key: PlanKey;
  name: string;
  monthlyUsdPerSeat: number;
  perks: string[];
  cta: string;
  highlight: boolean;
  footnote: string;
}

export function PricingPage() {
  const { t, i18n } = useTranslation();
  const [currency, setCurrency] = useState(currentDisplayCurrency());
  useDocumentMeta({
    title: 'Pricing — PatronEZ',
    description: 'Public per-seat pricing for PatronEZ. Free, Growth ($39), Global ($89), Enterprise ($169). Replaces Sansan + Apollo combined at ~70% lower cost.',
    path: '/pricing',
  });

  // Stripe-sourced live plan catalogue — single source of truth.
  const plansQ = useQuery({ queryKey: ['billing-plans'], queryFn: () => billingApi.plans(), staleTime: 5 * 60 * 1000 });
  const priceFor = (code: PlanKey): number => {
    const p = plansQ.data?.find((x) => x.code === code);
    return p ? p.monthlyUnitAmountCents / 100 : 0;
  };

  const plans: Plan[] = [
    {
      key: 'free',
      name: t('pricing.free'),
      monthlyUsdPerSeat: priceFor('free'),
      perks: [
        t('pricing.free_seats'),
        t('pricing.free_region'),
        t('pricing.free_contacts'),
        t('pricing.free_ai'),
      ],
      cta: t('pricing.cta_start_free', { defaultValue: 'Start free' }),
      highlight: false,
      footnote: t('pricing.free_foot'),
    },
    {
      key: 'growth',
      name: t('pricing.growth'),
      monthlyUsdPerSeat: priceFor('growth'),
      perks: [t('pricing.growth_perk')],
      cta: t('pricing.cta_start_growth', { defaultValue: 'Start Growth' }),
      highlight: false,
      footnote: t('pricing.per_seat_month'),
    },
    {
      key: 'global',
      name: t('pricing.global'),
      monthlyUsdPerSeat: priceFor('global'),
      perks: [t('pricing.global_perk')],
      cta: t('pricing.cta_start_global', { defaultValue: 'Start Global' }),
      highlight: true,
      footnote: t('pricing.per_seat_month'),
    },
    {
      key: 'enterprise',
      name: t('pricing.enterprise'),
      monthlyUsdPerSeat: priceFor('enterprise'),
      perks: [t('pricing.enterprise_perk')],
      cta: t('pricing.cta_try_enterprise', { defaultValue: 'Try Enterprise' }),
      highlight: false,
      footnote: t('pricing.per_seat_month'),
    },
  ];

  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-6xl px-6 pt-20 pb-24">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="display text-4xl md:text-5xl font-semibold tracking-tight text-ink-900 serif-jp">
              {t('pricing.title')}
            </h1>
            <p className="mt-3 text-ink-600 max-w-2xl">{t('pricing.intro')}</p>
          </div>
          <CurrencySwitcher onChange={setCurrency} />
        </div>

        {/* Live calculator — the "strongest wedge" per IMPLEMENTATION.md §4 */}
        <div className="mt-10">
          <PricingCalculator />
        </div>

        {/* Tier cards still rendered below for browsers who want a side-by-side */}
        <h2 className="display serif-jp mt-16 text-2xl md:text-3xl font-medium tracking-tight text-ink-900">
          {t('pricing.all_tiers_title')}
        </h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {plans.map((p) => (
            <PlanCard key={p.key} plan={p} displayCurrency={currency} locale={i18n.language} />
          ))}
        </div>

        <p className="mt-8 text-xs text-ink-500 max-w-3xl">{t('pricing.fine_print')}</p>
      </main>
      <SiteFooter />
    </div>
  );
}

function PlanCard({ plan, displayCurrency, locale }: { plan: Plan; displayCurrency: string; locale: string }) {
  const converted = fromUsd(plan.monthlyUsdPerSeat, displayCurrency);
  const priceLabel =
    plan.monthlyUsdPerSeat === 0
      ? formatDisplayCurrency(0, displayCurrency, locale)
      : formatDisplayCurrency(converted.amount, displayCurrency, locale);

  return (
    <div
      className={
        'rounded-2xl border bg-white p-6 flex flex-col ' +
        (plan.highlight ? 'border-accent-500 shadow-soft' : 'border-ink-200')
      }
    >
      <h3 className="text-lg font-semibold text-ink-900">{plan.name}</h3>
      <p className="mt-3 num text-3xl font-semibold text-ink-900">{priceLabel}</p>
      <p className="text-xs text-ink-500 mt-0.5">{plan.footnote}</p>
      <ul className="mt-4 space-y-2 text-sm text-ink-600 flex-1">
        {plan.perks.map((perk) => (
          <li key={perk}>· {perk}</li>
        ))}
      </ul>
      <button
        className={
          'mt-6 inline-flex items-center justify-center px-4 py-2.5 rounded-lg text-sm font-semibold transition ' +
          (plan.highlight
            ? 'bg-accent-500 text-white hover:bg-accent-600'
            : 'border border-ink-300 text-ink-900 hover:bg-ink-100')
        }
      >
        {plan.cta}
      </button>
    </div>
  );
}
